import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { translateZoneName, translateZoneNameToEnglish, clearTranslationCache } from '@medical/shared/utils/translateZoneName';
import i18n from '@medical/shared/i18n';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StatisticsContainer = styled.div`
  max-width: ${props => props.compact ? '100%' : '1200px'};
  margin: ${props => props.compact ? '0' : '0 auto'};
  padding: ${props => props.compact ? '0' : '2rem 1rem'};

  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0' : '1rem 0.5rem'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.compact ? '0' : '0.5rem 0.25rem'};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    flex-direction: column;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    gap: 0.75rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const VASCaption = styled.p`
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0.25rem 0 0 0;
  font-weight: 500;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const DateRangeSelector = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;

  label {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
    white-space: nowrap;

    @media (max-width: 480px) {
      font-size: 0.8125rem;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.625rem 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    padding: 0.5rem 0.75rem;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const ChartTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin: 0 0 0.75rem 0;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin: 0 0 0.5rem 0;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  height: 300px;

  @media (max-width: 768px) {
    height: 250px;
  }

  @media (max-width: 480px) {
    height: 200px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0.75rem;
  background: ${props => props.color || '#f3f4f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
    margin: 0 auto 0.75rem;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1.125rem;
    margin: 0 auto 0.5rem;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.375rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
  }
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

// VAS (Visual Analog Scale) 0–10 mapped to 5 categories: same order everywhere for alignment
const VAS_PAIN_LEVELS = ['no-pain', 'mild', 'moderate', 'severe', 'worst-pain'];
const VAS_LEVEL_LABEL_KEYS = { 'no-pain': 'noPain', 'mild': 'mild', 'moderate': 'moderatePain', 'severe': 'severePain', 'worst-pain': 'worstPain' };
const VAS_LEVEL_COLORS = { 'no-pain': '#10b981', 'mild': '#60a5fa', 'moderate': '#f59e0b', 'severe': '#f97316', 'worst-pain': '#ef4444' };

const Statistics = ({ patientId = null, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [statsData, setStatsData] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on language change
  
  // Listen to language changes and refresh
  useEffect(() => {
    const handleLanguageChange = () => {
      clearTranslationCache(); // Clear cache when language changes
      setRefreshKey(prev => prev + 1); // Force component re-render
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7'); // days
  const [selectedRegion, setSelectedRegion] = useState('');
  const [regionPainLogs, setRegionPainLogs] = useState([]);
  const [allZoneStats, setAllZoneStats] = useState([]); // Store all zones for dropdown

  // Use current language for statistics (same as rest of app)
  const getT = (key, options = {}) => t(key, options);

  useEffect(() => {
    fetchStatistics();
  }, [dateRange, patientId, selectedRegion]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const endDate = new Date();
      const startDate = subDays(endDate, parseInt(dateRange));
      
      let response;
      if (patientId) {
        // Fetch patient logs and calculate stats
        const token = localStorage.getItem('doctorToken');
        
        // First, fetch all logs to get all zones for dropdown
        const allLogsResponse = await axios.get(`/api/pain-logs/patient/${patientId}`, {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: 1000
          },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const allLogs = allLogsResponse.data.data?.painLogs || [];
        
        // Build all zone stats for dropdown
        const allZoneStatsMap = {};
        allLogs.forEach(log => {
          const zoneKey = log.zoneId || log.zoneName;
          if (!allZoneStatsMap[zoneKey]) {
            allZoneStatsMap[zoneKey] = {
              _id: zoneKey,
              zoneId: log.zoneId,
              zoneName: log.zoneName,
              painLevels: []
            };
          }
        });
        setAllZoneStats(Object.values(allZoneStatsMap));
        
        // Then, filter logs if region is selected
        let logs = allLogs;
        if (selectedRegion) {
          logs = allLogs.filter(log => 
            (log.zoneId || log.zoneName) === selectedRegion
          );
        }
        
        // Calculate statistics from logs
        const totalLogs = logs.length;
        const painLevelDistribution = [
          { _id: 'no-pain', count: logs.filter(l => l.painLevel === 'no-pain').length },
          { _id: 'mild', count: logs.filter(l => l.painLevel === 'mild').length },
          { _id: 'moderate', count: logs.filter(l => l.painLevel === 'moderate').length },
          { _id: 'severe', count: logs.filter(l => l.painLevel === 'severe').length },
          { _id: 'worst-pain', count: logs.filter(l => l.painLevel === 'worst-pain').length }
        ];
        
        // Group by date for daily trends
        const dailyTrends = [];
        const logsByDate = {};
        logs.forEach(log => {
          const dateKey = format(new Date(log.timestamp), 'yyyy-MM-dd');
          if (!logsByDate[dateKey]) {
            logsByDate[dateKey] = {};
          }
          if (!logsByDate[dateKey][log.painLevel]) {
            logsByDate[dateKey][log.painLevel] = 0;
          }
          logsByDate[dateKey][log.painLevel]++;
        });
        
        Object.keys(logsByDate).forEach(dateKey => {
          Object.keys(logsByDate[dateKey]).forEach(level => {
            dailyTrends.push({
              _id: { date: dateKey, painLevel: level },
              count: logsByDate[dateKey][level]
            });
          });
        });
        
        // Zone stats
        const zoneStats = {};
        logs.forEach(log => {
          const zoneKey = log.zoneId || log.zoneName;
          if (!zoneStats[zoneKey]) {
            zoneStats[zoneKey] = {
              zoneId: log.zoneId,
              zoneName: log.zoneName,
              painLevels: []
            };
          }
          const levelIndex = zoneStats[zoneKey].painLevels.findIndex(l => l.level === log.painLevel);
          if (levelIndex === -1) {
            zoneStats[zoneKey].painLevels.push({ level: log.painLevel, count: 1 });
          } else {
            zoneStats[zoneKey].painLevels[levelIndex].count++;
          }
        });
        
        const sortedZoneStats = Object.values(zoneStats).sort((a, b) => {
          const aTotal = a.painLevels.reduce((sum, l) => sum + l.count, 0);
          const bTotal = b.painLevels.reduce((sum, l) => sum + l.count, 0);
          return bTotal - aTotal;
        });
        
        setStatsData({
          totalLogs,
          painLevelDistribution,
          dailyTrends,
          zoneStats: sortedZoneStats
        });
        
        // Store all zones for dropdown
        setAllZoneStats(sortedZoneStats);
        
        // Store region pain logs if region is selected
        if (selectedRegion) {
          setRegionPainLogs(logs);
        } else {
          setRegionPainLogs([]);
        }
      } else {
        // Normal patient view
        // First, fetch all zone stats for the dropdown (without region filter)
        const allStatsResponse = await axios.get('/api/pain-logs/stats', {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        });
        const allStatsData = allStatsResponse.data || {};
        setAllZoneStats(allStatsData.zoneStats || []);
        
        // Then, if a region is selected, fetch filtered data
        if (selectedRegion) {
          const logsResponse = await axios.get('/api/pain-logs', {
            params: {
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              zoneId: selectedRegion,
              limit: 1000
            }
          });
          const logs = logsResponse.data.painLogs || [];
          
          // Calculate statistics from filtered logs
          const totalLogs = logs.length;
          const painLevelDistribution = [
            { _id: 'no-pain', count: logs.filter(l => l.painLevel === 'no-pain').length },
            { _id: 'mild', count: logs.filter(l => l.painLevel === 'mild').length },
            { _id: 'moderate', count: logs.filter(l => l.painLevel === 'moderate').length },
            { _id: 'severe', count: logs.filter(l => l.painLevel === 'severe').length },
            { _id: 'worst-pain', count: logs.filter(l => l.painLevel === 'worst-pain').length }
          ];
          
          // Group by date for daily trends
          const dailyTrends = [];
          const logsByDate = {};
          logs.forEach(log => {
            const dateKey = format(new Date(log.timestamp), 'yyyy-MM-dd');
            if (!logsByDate[dateKey]) {
              logsByDate[dateKey] = {};
            }
            if (!logsByDate[dateKey][log.painLevel]) {
              logsByDate[dateKey][log.painLevel] = 0;
            }
            logsByDate[dateKey][log.painLevel]++;
          });
          
          Object.keys(logsByDate).forEach(dateKey => {
            Object.keys(logsByDate[dateKey]).forEach(level => {
              dailyTrends.push({
                _id: { date: dateKey, painLevel: level },
                count: logsByDate[dateKey][level]
              });
            });
          });
          
          // Zone stats (will be single zone when filtered)
          const zoneStats = [];
          if (logs.length > 0) {
            const zoneKey = logs[0].zoneId || logs[0].zoneName;
            const zoneStatsObj = {
              _id: zoneKey,
              zoneId: logs[0].zoneId,
              zoneName: logs[0].zoneName,
              painLevels: []
            };
            
            logs.forEach(log => {
              const levelIndex = zoneStatsObj.painLevels.findIndex(l => l.level === log.painLevel);
              if (levelIndex === -1) {
                zoneStatsObj.painLevels.push({ level: log.painLevel, count: 1 });
              } else {
                zoneStatsObj.painLevels[levelIndex].count++;
              }
            });
            
            zoneStats.push(zoneStatsObj);
          }
          
          setStatsData({
            totalLogs,
            painLevelDistribution,
            dailyTrends,
            zoneStats
          });
          setRegionPainLogs(logs);
        } else {
          // No region selected - use all stats
          setStatsData({
            totalLogs: allStatsData.totalLogs || 0,
            painLevelDistribution: allStatsData.painLevelDistribution || [],
            dailyTrends: allStatsData.dailyTrends || [],
            zoneStats: allStatsData.zoneStats || []
          });
          setRegionPainLogs([]);
        }
      }
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError(getT('statistics.loadFailed'));
    } finally {
      setLoading(false);
    }
  };


  const getIntensityLevel = (count, total) => {
    const percentage = (count / total) * 100;
    if (percentage >= 50) return 'high';
    if (percentage >= 20) return 'medium';
    if (percentage > 0) return 'low';
    return 'none';
  };

  const prepareRegionTimelineData = () => {
    if (!regionPainLogs || regionPainLogs.length === 0) return null;

    // Sort logs by timestamp
    const sortedLogs = [...regionPainLogs].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    // Map pain levels to numeric values for the chart (5 VAS categories, 0–4)
    const painLevelMap = Object.fromEntries(VAS_PAIN_LEVELS.map((level, i) => [level, i]));

    // Prepare data points
    const labels = sortedLogs.map(log => 
      format(new Date(log.timestamp), 'MMM dd, HH:mm')
    );
    
    const painIntensityData = sortedLogs.map(log => painLevelMap[log.painLevel] ?? log.painIntensity ?? 0);
    
    const pointBackgroundColors = sortedLogs.map(log => VAS_LEVEL_COLORS[log.painLevel] || '#6b7280');

    const pointBorderColors = pointBackgroundColors;

    return {
      labels,
      datasets: [
        {
          label: getT('statistics.labels.intensityOverTime'),
          data: painIntensityData,
          borderColor: '#0B63C5',
          backgroundColor: 'rgba(102, 126, 234, 0.1)',
          pointBackgroundColor: pointBackgroundColors,
          pointBorderColor: pointBorderColors,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 2,
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const regionTimelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const index = context.dataIndex;
            const log = regionPainLogs[index];
            const labelKey = VAS_LEVEL_LABEL_KEYS[log.painLevel];
            const label = labelKey ? getT(`statistics.labels.${labelKey}`) : log.painLevel;
            return [
              `Pain Level: ${label}`,
              `Time: ${format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm')}`,
              log.notes ? `Notes: ${log.notes}` : null
            ].filter(Boolean);
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 4,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            const level = VAS_PAIN_LEVELS[value];
            return level ? getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[level]}`) : '';
          }
        },
        title: {
          display: true,
          text: getT('statistics.labels.painLevel')
        }
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 10
          }
        },
        title: {
          display: true,
          text: getT('statistics.labels.time')
        }
      }
    }
  };

  const prepareLineChartData = () => {
    if (!statsData?.dailyTrends) return null;

    const labels = (statsData?.dailyTrends || []).map(item => 
      format(new Date(item._id.date), 'MMM dd')
    ).filter((value, index, self) => self.indexOf(value) === index);

    const datasets = VAS_PAIN_LEVELS.map(level => {
      const data = labels.map(label => {
        const dayData = statsData?.dailyTrends?.find(item => 
          format(new Date(item._id.date), 'MMM dd') === label && 
          item._id.painLevel === level
        );
        return dayData ? dayData.count : 0;
      });
      const color = VAS_LEVEL_COLORS[level];
      return {
        label: getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[level]}`),
        data,
        borderColor: color,
        backgroundColor: color + '20',
        tension: 0.4
      };
    });

    // Check if there's any actual data (not all zeros)
    const hasData = datasets.some(dataset => dataset.data.some(value => value > 0));

    if (!hasData) return null;

    return {
      labels,
      datasets
    };
  };

  const prepareBarChartData = () => {
    if (!statsData?.zoneStats) return null;

    // When a region is selected, show only that region's data
    // Otherwise show top 10 zones
    let zones;
    if (selectedRegion) {
      zones = statsData.zoneStats.filter(zone => 
        (zone._id || zone.zoneId || zone.zoneName) === selectedRegion
      );
      // If no matching zone found, return null
      if (zones.length === 0) return null;
    } else {
      zones = statsData.zoneStats.slice(0, 10) || []; // Top 10 zones
    }
    
    // Use current language for zone names in charts
    const labels = zones.map(zone => translateZoneName(zone.zoneName, t));
    
    // VAS order: no-pain (bottom) → worst-pain (top) for stacked bar alignment
    const datasets = VAS_PAIN_LEVELS.map(level => ({
      label: getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[level]}`),
      data: zones.map(zone => {
        const l = zone.painLevels?.find(p => p.level === level);
        return l ? l.count : 0;
      }),
      backgroundColor: VAS_LEVEL_COLORS[level]
    }));

    return {
      labels,
      datasets
    };
  };

  const prepareDoughnutChartData = () => {
    if (!statsData?.painLevelDistribution) return null;

    // Always show all 5 VAS levels in order (no-pain → worst-pain) for consistent alignment
    const dist = statsData.painLevelDistribution;
    const byLevel = Object.fromEntries(dist.map(item => [item._id, item]));
    const data = VAS_PAIN_LEVELS.map(id => (byLevel[id]?.count) ?? 0);
    const labels = VAS_PAIN_LEVELS.map(id => getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[id]}`) || id);
    const backgroundColor = VAS_PAIN_LEVELS.map(id => VAS_LEVEL_COLORS[id] || '#6b7280');

    return {
      labels,
      datasets: [{
        data,
        backgroundColor,
        borderWidth: 0
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 12
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 6
        }
      },
    },
  };

  if (loading) {
    return <LoadingSpinner text={getT('statistics.loading')} />;
  }

  if (error) {
    return (
      <StatisticsContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{getT('statistics.error')}</h2>
          <p>{error}</p>
          <button onClick={fetchStatistics} className="btn btn-primary">
            {getT('dashboard.actions.retry')}
          </button>
        </div>
      </StatisticsContainer>
    );
  }

  // Check if there's actually any data - consider all possible data sources
  const hasData = statsData && (
    (statsData.totalLogs > 0) ||
    (statsData.painLevelDistribution && statsData.painLevelDistribution.length > 0 && statsData.painLevelDistribution.some(p => p.count > 0)) ||
    (statsData.dailyTrends && statsData.dailyTrends.length > 0) ||
    (statsData.zoneStats && statsData.zoneStats.length > 0)
  );

  if (!statsData || !hasData) {
    return (
      <StatisticsContainer>
        <Header>
          <PageTitle>{getT('statistics.title')}</PageTitle>
          <DateRangeSelector>
            <label htmlFor="dateRange">{getT('statistics.timePeriod', { defaultValue: 'Time Period' })}:</label>
            <Select
              id="dateRange"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="7">{getT('statistics.lastDays', { count: 7, defaultValue: 'Last 7 days' })}</option>
              <option value="14">{getT('statistics.lastDays', { count: 14, defaultValue: 'Last 14 days' })}</option>
              <option value="30">{getT('statistics.lastDays', { count: 30, defaultValue: 'Last 30 days' })}</option>
              <option value="90">{getT('statistics.lastDays', { count: 90, defaultValue: 'Last 90 days' })}</option>
            </Select>
            
            <div style={{ 
              marginLeft: '1rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              flexShrink: 0,
              visibility: 'visible',
              opacity: 1
            }}>
              <label htmlFor="regionSelect" style={{ 
                whiteSpace: 'nowrap',
                fontWeight: 500,
                color: '#374151',
                fontSize: '0.875rem'
              }}>
                {getT('statistics.filterByRegion', { defaultValue: 'Filter by Region' })}:
              </label>
              <Select
                id="regionSelect"
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                style={{ 
                  minWidth: '200px',
                  visibility: 'visible',
                  display: 'block',
                  opacity: 1
                }}
                disabled={!allZoneStats.length && !(statsData?.zoneStats && statsData.zoneStats.length > 0)}
              >
                <option value="">{getT('statistics.allRegions', { defaultValue: 'All Regions' })}</option>
                {(allZoneStats.length > 0 ? allZoneStats : (statsData?.zoneStats || []))
                  .filter(zone => (zone._id || zone.zoneId || zone.zoneName))
                  .filter((zone, idx, arr) => arr.findIndex(z => (z._id || z.zoneId || z.zoneName) === (zone._id || zone.zoneId || zone.zoneName)) === idx)
                  .map(zone => (
                    <option key={zone._id || zone.zoneId || zone.zoneName} value={zone._id || zone.zoneId || zone.zoneName}>
                      {translateZoneName(zone.zoneName, t)}
                    </option>
                  ))}
              </Select>
            </div>
          </DateRangeSelector>
        </Header>
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>{getT('statistics.empty', { defaultValue: 'No data available' })}</EmptyTitle>
          <EmptyText>{getT('statistics.emptyText', { defaultValue: `No pain logs found for the selected time period (last ${dateRange} days). Try selecting a longer time period or start tracking your pain to see statistics and trends.` })}</EmptyText>
        </EmptyState>
      </StatisticsContainer>
    );
  }

  const lineChartData = prepareLineChartData();
  const barChartData = prepareBarChartData();
  const doughnutChartData = prepareDoughnutChartData();
  const regionTimelineData = prepareRegionTimelineData();

  // Get available regions from zoneStats
  // API returns zoneStats with _id as zoneId and zoneName as a field
  const availableRegions = statsData?.zoneStats?.map(zone => ({
    zoneId: zone._id || zone.zoneId || zone.zoneName,
    zoneName: zone.zoneName
  })).filter(region => region.zoneId && region.zoneName) || [];

  return (
    <StatisticsContainer compact={compact}>
      <Header>
        <div>
          {!compact && <PageTitle>{getT('statistics.title')}</PageTitle>}
          {!compact && (
            <VASCaption>
              {getT('statistics.vasScaleCaption', { defaultValue: 'Pain levels: VAS 0–10 — No Pain · Mild (1–3) · Moderate (4–6) · Severe (7–9) · Worst (10)' })}
            </VASCaption>
          )}
        </div>
        <DateRangeSelector>
          <label htmlFor="dateRange">{getT('statistics.timePeriod', { defaultValue: 'Time Period' })}:</label>
          <Select
            id="dateRange"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="7">{getT('statistics.lastDays', { count: 7, defaultValue: 'Last 7 days' })}</option>
            <option value="14">{getT('statistics.lastDays', { count: 14, defaultValue: 'Last 14 days' })}</option>
            <option value="30">{getT('statistics.lastDays', { count: 30, defaultValue: 'Last 30 days' })}</option>
            <option value="90">{getT('statistics.lastDays', { count: 90, defaultValue: 'Last 90 days' })}</option>
          </Select>
          
          <div style={{ 
            marginLeft: '1rem', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            flexShrink: 0,
            visibility: 'visible',
            opacity: 1
          }}>
            <label htmlFor="regionSelect" style={{ 
              whiteSpace: 'nowrap',
              fontWeight: 500,
              color: '#374151',
              fontSize: '0.875rem'
            }}>
              {getT('statistics.filterByRegion', { defaultValue: 'Filter by Region' })}:
            </label>
            <Select
              id="regionSelect"
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              style={{ 
                minWidth: '200px',
                visibility: 'visible',
                display: 'block',
                opacity: 1
              }}
              disabled={!allZoneStats.length && !(statsData?.zoneStats && statsData.zoneStats.length > 0)}
            >
              <option value="">{getT('statistics.allRegions', { defaultValue: 'All Regions' })}</option>
              {(allZoneStats.length > 0 ? allZoneStats : (statsData?.zoneStats || []))
                .filter(zone => (zone._id || zone.zoneId || zone.zoneName))
                .filter((zone, idx, arr) => arr.findIndex(z => (z._id || z.zoneId || z.zoneName) === (zone._id || zone.zoneId || zone.zoneName)) === idx)
                .map(zone => (
                  <option key={zone._id || zone.zoneId || zone.zoneName} value={zone._id || zone.zoneId || zone.zoneName}>
                    {translateZoneName(zone.zoneName, t)}
                  </option>
                ))}
            </Select>
          </div>
        </DateRangeSelector>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatIcon color="#3b82f6">📊</StatIcon>
          <StatValue>{statsData?.totalLogs || 0}</StatValue>
          <StatLabel>{getT('statistics.totalLogs', { defaultValue: 'Total Logs' })}</StatLabel>
        </StatCard>
        {VAS_PAIN_LEVELS.map((level) => (
          <StatCard key={level}>
            <StatIcon color={VAS_LEVEL_COLORS[level]}>
              {level === 'no-pain' ? '🟢' : level === 'mild' ? '🔵' : level === 'moderate' ? '🟡' : level === 'severe' ? '🟠' : '🔴'}
            </StatIcon>
            <StatValue>
              {statsData?.painLevelDistribution?.find(p => p._id === level)?.count || 0}
            </StatValue>
            <StatLabel>{getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[level]}`)}</StatLabel>
          </StatCard>
        ))}
      </StatsGrid>

      <ChartsGrid>
        {lineChartData ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painTrends', { defaultValue: 'Pain Trends Over Time' })} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painTrends', { defaultValue: 'Pain Trends Over Time' })
              }
            </ChartTitle>
            <ChartContainer>
              <Line data={lineChartData} options={chartOptions} />
            </ChartContainer>
          </ChartCard>
        ) : statsData?.dailyTrends ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painTrends', { defaultValue: 'Pain Trends Over Time' })} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painTrends', { defaultValue: 'Pain Trends Over Time' })
              }
            </ChartTitle>
            <ChartContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
              <EmptyState style={{ minHeight: 'unset', padding: '1rem' }}>
                <EmptyIcon>📊</EmptyIcon>
                <EmptyTitle>{getT('statistics.noDataForRegion')}</EmptyTitle>
                <EmptyText>
                  {selectedRegion 
                    ? getT('statistics.emptyRegionLine', { zone: translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region') })
                    : getT('statistics.noPainTrendsData')
                  }
                </EmptyText>
              </EmptyState>
            </ChartContainer>
          </ChartCard>
        ) : null}

        {barChartData ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painByZone', { defaultValue: 'Pain by Body Zone' })} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painByZone', { defaultValue: 'Pain by Body Zone' })
              }
            </ChartTitle>
            <ChartContainer>
              <Bar data={barChartData} options={chartOptions} />
            </ChartContainer>
          </ChartCard>
        ) : statsData?.zoneStats ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painByZone', { defaultValue: 'Pain by Body Zone' })} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painByZone', { defaultValue: 'Pain by Body Zone' })
              }
            </ChartTitle>
            <ChartContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
              <EmptyState style={{ minHeight: 'unset', padding: '1rem' }}>
                <EmptyIcon>📊</EmptyIcon>
                <EmptyTitle>{getT('statistics.noDataForRegion')}</EmptyTitle>
                <EmptyText>
                  {selectedRegion 
                    ? getT('statistics.emptyRegionBar', { zone: translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region') })
                    : getT('statistics.noPainByZoneData')
                  }
                </EmptyText>
              </EmptyState>
            </ChartContainer>
          </ChartCard>
        ) : null}

        {doughnutChartData ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painLevelDistribution')} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painLevelDistribution')
              }
            </ChartTitle>
            <ChartContainer>
              <Doughnut data={doughnutChartData} options={doughnutOptions} />
            </ChartContainer>
          </ChartCard>
        ) : statsData?.painLevelDistribution ? (
          <ChartCard>
            <ChartTitle>
              {selectedRegion 
                ? `${getT('statistics.painLevelDistribution')} - ${translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region')}`
                : getT('statistics.painLevelDistribution')
              }
            </ChartTitle>
            <ChartContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
              <EmptyState style={{ minHeight: 'unset', padding: '1rem' }}>
                <EmptyIcon>📊</EmptyIcon>
                <EmptyTitle>{getT('statistics.noDataForRegion')}</EmptyTitle>
                <EmptyText>
                  {selectedRegion 
                    ? getT('statistics.emptyRegionDoughnut', { zone: translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region') })
                    : getT('statistics.noPainLevelDistributionData')
                  }
                </EmptyText>
              </EmptyState>
            </ChartContainer>
          </ChartCard>
        ) : null}
      </ChartsGrid>

      {/* Region-Based Pain Timeline - Only show when a region is selected */}
      {selectedRegion && regionTimelineData && (
        <ChartCard style={{ marginTop: '2rem' }}>
          <ChartTitle>{getT('statistics.painTimelineFor', { zone: translateZoneNameToEnglish(statsData?.zoneStats?.find(z => (z._id || z.zoneId || z.zoneName) === selectedRegion)?.zoneName || 'Selected Region') })}</ChartTitle>
          <ChartContainer style={{ height: '400px' }}>
            <Line data={regionTimelineData} options={regionTimelineOptions} />
          </ChartContainer>
          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '0.5rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              <strong>{getT('statistics.totalEntries')}:</strong> {regionPainLogs.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {VAS_PAIN_LEVELS.map(level => (
                <span key={level}>
                  <strong>{getT(`statistics.labels.${VAS_LEVEL_LABEL_KEYS[level]}`)}:</strong>{' '}
                  {regionPainLogs.filter(l => l.painLevel === level).length}
                </span>
              ))}
            </div>
          </div>
        </ChartCard>
      )}

    </StatisticsContainer>
  );
};

export default Statistics;
